import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import { Package, Mail, MessageSquare, LogOut, Search, Download, Trash2, Check, Code, Users, Plus, Edit, X, Power, CheckCircle, Settings, Send, Wrench, Newspaper, Eye, EyeOff, Maximize2 } from 'lucide-react';
import APIBuilder from './APIBuilder';
import IntegrationsConfig from './IntegrationsConfig';
import AdminShipments from './AdminShipments';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Detail Modal Component
const DetailModal = ({ isOpen, onClose, title, data }) => {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-semibold">{title}</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4 overflow-y-auto max-h-[calc(80vh-120px)]">
          {data && Object.entries(data).map(([key, value]) => (
            <div key={key} className="mb-4">
              <label className="block text-sm font-medium text-gray-600 mb-1 capitalize">
                {key.replace(/_/g, ' ')}
              </label>
              <div className="p-3 bg-gray-50 rounded-lg text-sm whitespace-pre-wrap break-words">
                {key === 'created_at' || key === 'updated_at' 
                  ? new Date(value).toLocaleString('it-IT')
                  : String(value || '-')}
              </div>
            </div>
          ))}
        </div>
        <div className="p-4 border-t flex justify-end">
          <Button onClick={onClose}>Chiudi</Button>
        </div>
      </div>
    </div>
  );
};

const AdminPanel = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState(localStorage.getItem('admin_token') || '');

  const [demoRequests, setDemoRequests] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [newsletter, setNewsletter] = useState([]);
  const [apis, setApis] = useState([]);
  const [developers, setDevelopers] = useState([]);
  const [news, setNews] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Detail modal state
  const [detailModal, setDetailModal] = useState({ isOpen: false, title: '', data: null });
  
  // API Builder state
  const [apiBuilderOpen, setApiBuilderOpen] = useState(false);
  const [editingApi, setEditingApi] = useState(null);
  
  // SMTP Configuration state
  const [smtpConfig, setSmtpConfig] = useState({
    smtp_host: '',
    smtp_port: 587,
    smtp_username: '',
    smtp_password: '',
    smtp_from_email: 'noreply@spedireonline.net',
    smtp_from_name: 'SpedireOnline.net',
    use_tls: true,
    is_active: false,
    notification_emails: []
  });
  const [testEmail, setTestEmail] = useState('');
  const [smtpLoading, setSmtpLoading] = useState(false);
  const [newNotifEmail, setNewNotifEmail] = useState('');
  const [notifLoading, setNotifLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, {
        username,
        password
      });

      const accessToken = response.data.access_token;
      setToken(accessToken);
      localStorage.setItem('admin_token', accessToken);
      setIsAuthenticated(true);
      toast.success('Login effettuato con successo!');
      // Pass token directly since state update is async
      loadAllData(accessToken);
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.detail || 'Credenziali errate');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = useCallback(() => {
    setToken('');
    localStorage.removeItem('admin_token');
    setIsAuthenticated(false);
    setUsername('');
    setPassword('');
    toast.success('Logout effettuato');
  }, []);

  const loadAllData = useCallback(async (authToken = null) => {
    const currentToken = authToken || token;
    if (!currentToken) {
      console.error('No token available for loadAllData');
      return;
    }
    
    try {
      const headers = { Authorization: `Bearer ${currentToken}` };

      // Use Promise.allSettled to handle partial failures
      const results = await Promise.allSettled([
        axios.get(`${API}/admin/demo-requests`, { headers }),
        axios.get(`${API}/admin/contacts`, { headers }),
        axios.get(`${API}/admin/newsletter`, { headers }),
        axios.get(`${API}/admin/apis`, { headers }),
        axios.get(`${API}/admin/developers`, { headers }),
        axios.get(`${API}/admin/smtp-config`, { headers }),
        axios.get(`${API}/admin/news`, { headers })
      ]);

      // Extract data safely, using empty arrays/objects as fallback
      const [demoRes, contactsRes, newsletterRes, apisRes, devsRes, smtpRes, newsRes] = results;

      if (demoRes.status === 'fulfilled') setDemoRequests(demoRes.value.data || []);
      if (contactsRes.status === 'fulfilled') setContacts(contactsRes.value.data || []);
      if (newsletterRes.status === 'fulfilled') setNewsletter(newsletterRes.value.data || []);
      if (apisRes.status === 'fulfilled') setApis(apisRes.value.data || []);
      if (devsRes.status === 'fulfilled') setDevelopers(devsRes.value.data || []);
      if (newsRes.status === 'fulfilled') setNews(newsRes.value.data || []);
      
      if (smtpRes.status === 'fulfilled' && smtpRes.value.data) {
        setSmtpConfig({
          smtp_host: smtpRes.value.data.smtp_host || '',
          smtp_port: smtpRes.value.data.smtp_port || 587,
          smtp_username: smtpRes.value.data.smtp_username || '',
          smtp_password: '',
          smtp_from_email: smtpRes.value.data.smtp_from_email || 'noreply@spedireonline.net',
          smtp_from_name: smtpRes.value.data.smtp_from_name || 'SpedireOnline.net',
          use_tls: smtpRes.value.data.use_tls !== undefined ? smtpRes.value.data.use_tls : true,
          is_active: smtpRes.value.data.is_active || false,
          notification_emails: smtpRes.value.data.notification_emails || []
        });
      }

      // Check if any critical requests failed with 401
      const authFailed = results.some(r => r.status === 'rejected' && r.reason?.response?.status === 401);
      if (authFailed) {
        handleLogout();
        toast.error('Sessione scaduta, effettua di nuovo il login');
        return;
      }

      // Log any other failures for debugging
      results.forEach((r, i) => {
        if (r.status === 'rejected') {
          console.error(`Request ${i} failed:`, r.reason);
        }
      });

    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Errore nel caricamento dei dati');
    }
  }, [token, handleLogout]);

  useEffect(() => {
    if (token) {
      setIsAuthenticated(true);
      loadAllData();
    }
  }, [token, loadAllData]);

  const filterData = (data, fields) => {
    if (!searchTerm) return data;
    return data.filter(item =>
      fields.some(field =>
        item[field]?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  };

  const exportToCSV = (data, filename) => {
    if (data.length === 0) {
      toast.error('Nessun dato da esportare');
      return;
    }

    // Get headers from first object
    const headers = Object.keys(data[0]);
    
    // Create CSV content
    const csvContent = [
      headers.join(','),
      ...data.map(row => 
        headers.map(header => {
          const value = row[header] || '';
          // Escape commas and quotes
          return `"${String(value).replace(/"/g, '""')}"`;
        }).join(',')
      )
    ].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success('CSV esportato con successo!');
  };

  const updateStatus = async (type, id, newStatus) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const endpoint = type === 'demo' ? 'demo-requests' : 'contacts';
      
      await axios.patch(
        `${API}/admin/${endpoint}/${id}`,
        { id, status: newStatus },
        { headers }
      );
      
      toast.success('Status aggiornato con successo!');
      loadAllData();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Errore nell\'aggiornamento dello status');
    }
  };

  const deleteRecord = async (type, id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo record?')) {
      return;
    }

    try {
      const headers = { Authorization: `Bearer ${token}` };
      const endpoint = 
        type === 'demo' ? 'demo-requests' : 
        type === 'contact' ? 'contacts' : 
        'newsletter';
      
      await axios.delete(`${API}/admin/${endpoint}/${id}`, { headers });
      
      toast.success('Record eliminato con successo!');
      loadAllData();
    } catch (error) {
      console.error('Error deleting record:', error);
      toast.error('Errore nell\'eliminazione del record');
    }
  };

  // API Management Functions
  const handleSaveApi = async (apiData) => {
    setLoading(true);
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      if (editingApi) {
        // Update existing API
        await axios.put(`${API}/admin/apis/${editingApi.id}`, apiData, { headers });
        toast.success('API aggiornata con successo!');
      } else {
        // Create new API
        await axios.post(`${API}/admin/apis`, apiData, { headers });
        toast.success('API creata con successo!');
      }
      
      setApiBuilderOpen(false);
      setEditingApi(null);
      loadAllData();
    } catch (error) {
      console.error('Error saving API:', error);
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio dell\'API');
    } finally {
      setLoading(false);
    }
  };

  const handleEditApi = (api) => {
    setEditingApi(api);
    setApiBuilderOpen(true);
  };

  const toggleApiStatus = async (apiId) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.patch(`${API}/admin/apis/${apiId}/status`, {}, { headers });
      toast.success('Status API aggiornato!');
      loadAllData();
    } catch (error) {
      console.error('Error toggling API status:', error);
      toast.error('Errore nell\'aggiornamento dello status');
    }
  };

  const deleteApi = async (apiId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa API?')) {
      return;
    }

    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/admin/apis/${apiId}`, { headers });
      toast.success('API eliminata con successo!');
      loadAllData();
    } catch (error) {
      console.error('Error deleting API:', error);
      toast.error('Errore nell\'eliminazione dell\'API');
    }
  };

  // Developer Management Functions
  const approveDeveloper = async (developerId) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.patch(`${API}/admin/developers/${developerId}/approve`, {}, { headers });
      toast.success('Sviluppatore approvato!');
      loadAllData();
    } catch (error) {
      console.error('Error approving developer:', error);
      toast.error('Errore nell\'approvazione');
    }
  };

  const toggleDeveloperStatus = async (developerId) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.patch(`${API}/admin/developers/${developerId}/status`, {}, { headers });
      toast.success('Status sviluppatore aggiornato!');
      loadAllData();
    } catch (error) {
      console.error('Error toggling developer status:', error);
      toast.error('Errore nell\'aggiornamento dello status');
    }
  };

  const deleteDeveloper = async (developerId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo sviluppatore?')) {
      return;
    }

    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/admin/developers/${developerId}`, { headers });
      toast.success('Sviluppatore eliminato con successo!');
      loadAllData();
    } catch (error) {
      console.error('Error deleting developer:', error);
      toast.error('Errore nell\'eliminazione dello sviluppatore');
    }
  };

  // SMTP Management Functions
  const handleSaveSMTPConfig = async (e) => {
    e.preventDefault();
    setSmtpLoading(true);
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/admin/smtp-config`, smtpConfig, { headers });
      toast.success('Configurazione SMTP salvata con successo!');
      loadAllData();
    } catch (error) {
      console.error('Error saving SMTP config:', error);
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio della configurazione SMTP');
    } finally {
      setSmtpLoading(false);
    }
  };

  const handleTestSMTP = async () => {
    if (!testEmail) {
      toast.error('Inserisci un indirizzo email per il test');
      return;
    }

    setSmtpLoading(true);
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.post(
        `${API}/admin/smtp-config/test`,
        { recipient_email: testEmail },
        { headers }
      );
      toast.success(response.data.message || 'Email di test inviata con successo!');
      setTestEmail('');
    } catch (error) {
      console.error('Error testing SMTP:', error);
      toast.error(error.response?.data?.detail || 'Errore nell\'invio dell\'email di test');
    } finally {
      setSmtpLoading(false);
    }
  };

  const toggleSMTPStatus = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.patch(`${API}/admin/smtp-config/toggle`, {}, { headers });
      toast.success('Status SMTP aggiornato!');
      loadAllData();
    } catch (error) {
      console.error('Error toggling SMTP status:', error);
      toast.error('Errore nell\'aggiornamento dello status');
    }
  };

  // Notification recipient emails
  const addNotificationEmail = () => {
    const email = newNotifEmail.trim();
    if (!email) return;
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!re.test(email)) {
      toast.error('Email non valida');
      return;
    }
    if (smtpConfig.notification_emails.some(e => e.toLowerCase() === email.toLowerCase())) {
      toast.error('Email già presente');
      return;
    }
    setSmtpConfig({ ...smtpConfig, notification_emails: [...smtpConfig.notification_emails, email] });
    setNewNotifEmail('');
  };

  const removeNotificationEmail = (email) => {
    setSmtpConfig({ ...smtpConfig, notification_emails: smtpConfig.notification_emails.filter(e => e !== email) });
  };

  const handleSaveNotificationEmails = async () => {
    setNotifLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.patch(`${API}/admin/smtp-config/notifications`, { notification_emails: smtpConfig.notification_emails }, { headers });
      toast.success('Email di notifica salvate!');
      loadAllData();
    } catch (error) {
      console.error('Error saving notification emails:', error);
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio delle email di notifica');
    } finally {
      setNotifLoading(false);
    }
  };


  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg-section)' }}>
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_ship-smart-6/artifacts/4h2rj0xf_logoonly.png" 
              alt="spedireonline.net" 
              className="h-16 w-16 object-contain mx-auto mb-4"
            />
            <CardTitle style={{ color: 'var(--text-primary)' }}>
              Admin Panel
            </CardTitle>
            <p className="text-sm mt-2" style={{ color: 'var(--text-secondary)' }}>spedireonline.net</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Username</label>
                <Input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  placeholder="admin"
                />
              </div>
              <div>
                <label className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Password</label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="••••••••"
                />
              </div>
              <Button type="submit" className="w-full btn-primary" disabled={loading}>
                {loading ? 'Accesso in corso...' : 'Accedi'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-section)', padding: '2rem' }}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_ship-smart-6/artifacts/4h2rj0xf_logoonly.png" 
              alt="spedireonline.net" 
              className="h-12 w-12 object-contain"
            />
            <div>
              <h1 className="text-3xl font-bold" style={{ color: 'var(--text-primary)' }}>
                Admin Panel
              </h1>
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>spedireonline.net</p>
            </div>
          </div>
          <Button onClick={handleLogout} className="btn-secondary">
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>

        {/* Search Bar */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5" style={{ color: 'var(--text-muted)' }} />
              <Input
                type="text"
                placeholder="Cerca per nome, email, messaggio..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </CardContent>
        </Card>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Richieste Demo</p>
                  <p className="text-3xl font-bold mt-2" style={{ color: 'var(--accent-primary)' }}>
                    {demoRequests.length}
                  </p>
                </div>
                <Package className="w-12 h-12" style={{ color: 'var(--accent-primary)', opacity: 0.2 }} />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Messaggi Contatto</p>
                  <p className="text-3xl font-bold mt-2" style={{ color: '#3B82F6' }}>
                    {contacts.length}
                  </p>
                </div>
                <MessageSquare className="w-12 h-12" style={{ color: '#3B82F6', opacity: 0.2 }} />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Iscritti Newsletter</p>
                  <p className="text-3xl font-bold mt-2" style={{ color: '#8B5CF6' }}>
                    {newsletter.length}
                  </p>
                </div>
                <Mail className="w-12 h-12" style={{ color: '#8B5CF6', opacity: 0.2 }} />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Data Tables */}
        <Tabs defaultValue="news" className="w-full">
          <TabsList className="grid w-full grid-cols-9">
            <TabsTrigger value="news">
              <Newspaper className="w-4 h-4 mr-2" />
              Novità
            </TabsTrigger>
            <TabsTrigger value="shipments">
              <Package className="w-4 h-4 mr-2" />
              Spedizioni
            </TabsTrigger>
            <TabsTrigger value="demo">Richieste Demo</TabsTrigger>
            <TabsTrigger value="contacts">Contatti</TabsTrigger>
            <TabsTrigger value="newsletter">Newsletter</TabsTrigger>
            <TabsTrigger value="apis">
              <Code className="w-4 h-4 mr-2" />
              API
            </TabsTrigger>
            <TabsTrigger value="developers">
              <Users className="w-4 h-4 mr-2" />
              Sviluppatori
            </TabsTrigger>
            <TabsTrigger value="smtp">
              <Settings className="w-4 h-4 mr-2" />
              SMTP
            </TabsTrigger>
            <TabsTrigger value="integrations">
              <Wrench className="w-4 h-4 mr-2" />
              Integrazioni
            </TabsTrigger>
          </TabsList>

          {/* News/Novità Tab */}
          <TabsContent value="news">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle style={{ color: 'var(--text-primary)' }}>
                  <Newspaper className="w-5 h-5 inline mr-2" />
                  Gestione Novità
                </CardTitle>
              </CardHeader>
              <CardContent>
                {news.length === 0 ? (
                  <p className="text-center py-8" style={{ color: 'var(--text-muted)' }}>
                    Nessuna novità configurata
                  </p>
                ) : (
                  <div className="space-y-4">
                    {news.map((item) => (
                      <div 
                        key={item.id} 
                        className="flex items-center justify-between p-4 rounded-lg border"
                        style={{ 
                          borderColor: item.is_active ? 'var(--accent-primary)' : 'var(--border-color)',
                          background: item.is_active ? 'var(--accent-wash)' : 'var(--bg-card)'
                        }}
                      >
                        <div className="flex items-center gap-4">
                          {item.images?.[0] && (
                            <img 
                              src={item.images[0].url} 
                              alt={item.images[0].alt}
                              className="w-16 h-12 object-cover rounded"
                            />
                          )}
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold" style={{ color: 'var(--text-primary)' }}>
                                {item.title}
                              </h3>
                              {item.badge_text && (
                                <span 
                                  className="text-xs px-2 py-1 rounded-full"
                                  style={{ background: item.badge_color, color: '#000' }}
                                >
                                  {item.badge_text}
                                </span>
                              )}
                            </div>
                            <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
                              {item.description.substring(0, 100)}...
                            </p>
                            <div className="flex items-center gap-4 mt-2 text-xs" style={{ color: 'var(--text-muted)' }}>
                              <span>Sezione: {item.section_key}</span>
                              <span>Ordine: {item.order}</span>
                              <span>Features: {item.features?.length || 0}</span>
                              <span>Immagini: {item.images?.length || 0}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant={item.is_active ? "default" : "outline"}
                            size="sm"
                            onClick={async () => {
                              try {
                                const headers = { Authorization: `Bearer ${token}` };
                                await axios.patch(`${API}/admin/news/${item.id}/toggle`, {}, { headers });
                                toast.success(item.is_active ? 'Novità disattivata' : 'Novità attivata');
                                loadAllData();
                              } catch (error) {
                                toast.error('Errore durante l\'operazione');
                              }
                            }}
                          >
                            {item.is_active ? (
                              <>
                                <Eye className="w-4 h-4 mr-1" />
                                Attiva
                              </>
                            ) : (
                              <>
                                <EyeOff className="w-4 h-4 mr-1" />
                                Inattiva
                              </>
                            )}
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={async () => {
                              if (window.confirm('Eliminare questa novità?')) {
                                try {
                                  const headers = { Authorization: `Bearer ${token}` };
                                  await axios.delete(`${API}/admin/news/${item.id}`, { headers });
                                  toast.success('Novità eliminata');
                                  loadAllData();
                                } catch (error) {
                                  toast.error('Errore durante l\'eliminazione');
                                }
                              }
                            }}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                <div className="mt-6 p-4 rounded-lg" style={{ background: 'var(--bg-section)', border: '1px solid var(--border-color)' }}>
                  <h4 className="font-medium mb-2" style={{ color: 'var(--text-primary)' }}>Informazioni</h4>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                    Le novità attive vengono mostrate nella sezione "Novità" della homepage. 
                    Puoi attivare/disattivare singole novità per controllarle dal pannello admin.
                    La novità "Logistica Immersiva" è attualmente la sezione VR con Meta Quest 3.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="demo">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle style={{ color: 'var(--text-primary)' }}>Richieste Demo</CardTitle>
                <Button 
                  onClick={() => exportToCSV(demoRequests, 'demo_requests')} 
                  className="btn-secondary"
                  size="sm"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Esporta CSV
                </Button>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b" style={{ borderColor: 'var(--border-light)' }}>
                        <th className="text-left py-3 px-4 text-sm font-semibold" style={{ color: 'var(--text-secondary)' }}>Nome</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold" style={{ color: 'var(--text-secondary)' }}>Email</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold" style={{ color: 'var(--text-secondary)' }}>Azienda</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold" style={{ color: 'var(--text-secondary)' }}>Telefono</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold" style={{ color: 'var(--text-secondary)' }}>Status</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold" style={{ color: 'var(--text-secondary)' }}>Data</th>
                        <th className="text-center py-3 px-4 text-sm font-semibold" style={{ color: 'var(--text-secondary)' }}>Azioni</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filterData(demoRequests, ['name', 'email', 'company']).map((request) => (
                        <tr key={request.id} className="border-b hover:bg-opacity-50" style={{ borderColor: 'var(--border-light)' }}>
                          <td className="py-3 px-4" style={{ color: 'var(--text-body)' }}>{request.name}</td>
                          <td className="py-3 px-4" style={{ color: 'var(--text-body)' }}>{request.email}</td>
                          <td className="py-3 px-4" style={{ color: 'var(--text-body)' }}>{request.company || '-'}</td>
                          <td className="py-3 px-4" style={{ color: 'var(--text-body)' }}>{request.phone || '-'}</td>
                          <td className="py-3 px-4">
                            <select
                              value={request.status}
                              onChange={(e) => updateStatus('demo', request.id, e.target.value)}
                              className="text-xs px-2 py-1 rounded border"
                              style={{ borderColor: 'var(--border-light)' }}
                            >
                              <option value="pending">Pending</option>
                              <option value="contacted">Contattato</option>
                              <option value="completed">Completato</option>
                            </select>
                          </td>
                          <td className="py-3 px-4 text-sm" style={{ color: 'var(--text-muted)' }}>
                            {new Date(request.created_at).toLocaleDateString('it-IT')}
                          </td>
                          <td className="py-3 px-4 text-center">
                            <div className="flex items-center justify-center gap-1">
                              <Button
                                onClick={() => setDetailModal({ 
                                  isOpen: true, 
                                  title: `Richiesta Demo - ${request.name}`, 
                                  data: request 
                                })}
                                size="sm"
                                variant="ghost"
                                className="text-blue-500 hover:text-blue-700"
                                title="Visualizza dettagli"
                              >
                                <Maximize2 className="w-4 h-4" />
                              </Button>
                              <Button
                                onClick={() => deleteRecord('demo', request.id)}
                                size="sm"
                                variant="ghost"
                                className="text-red-500 hover:text-red-700"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {filterData(demoRequests, ['name', 'email', 'company']).length === 0 && (
                    <div className="text-center py-8" style={{ color: 'var(--text-muted)' }}>
                      Nessuna richiesta demo trovata
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="contacts">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle style={{ color: 'var(--text-primary)' }}>Messaggi di Contatto</CardTitle>
                <Button 
                  onClick={() => exportToCSV(contacts, 'contacts')} 
                  className="btn-secondary"
                  size="sm"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Esporta CSV
                </Button>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b" style={{ borderColor: 'var(--border-light)' }}>
                        <th className="text-left py-3 px-4 text-sm font-semibold" style={{ color: 'var(--text-secondary)' }}>Nome</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold" style={{ color: 'var(--text-secondary)' }}>Email</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold" style={{ color: 'var(--text-secondary)' }}>Messaggio</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold" style={{ color: 'var(--text-secondary)' }}>Status</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold" style={{ color: 'var(--text-secondary)' }}>Data</th>
                        <th className="text-center py-3 px-4 text-sm font-semibold" style={{ color: 'var(--text-secondary)' }}>Azioni</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filterData(contacts, ['name', 'email', 'message']).map((contact) => (
                        <tr key={contact.id} className="border-b hover:bg-opacity-50" style={{ borderColor: 'var(--border-light)' }}>
                          <td className="py-3 px-4" style={{ color: 'var(--text-body)' }}>{contact.name}</td>
                          <td className="py-3 px-4" style={{ color: 'var(--text-body)' }}>{contact.email}</td>
                          <td className="py-3 px-4" style={{ color: 'var(--text-body)' }}>
                            <div 
                              className="max-w-md cursor-pointer hover:text-blue-600" 
                              title="Clicca per leggere tutto"
                              onClick={() => setDetailModal({ 
                                isOpen: true, 
                                title: `Messaggio da ${contact.name}`, 
                                data: contact 
                              })}
                            >
                              {contact.message.length > 100 ? contact.message.substring(0, 100) + '...' : contact.message}
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <select
                              value={contact.status}
                              onChange={(e) => updateStatus('contact', contact.id, e.target.value)}
                              className="text-xs px-2 py-1 rounded border"
                              style={{ borderColor: 'var(--border-light)' }}
                            >
                              <option value="unread">Non letto</option>
                              <option value="read">Letto</option>
                              <option value="replied">Risposto</option>
                            </select>
                          </td>
                          <td className="py-3 px-4 text-sm" style={{ color: 'var(--text-muted)' }}>
                            {new Date(contact.created_at).toLocaleDateString('it-IT')}
                          </td>
                          <td className="py-3 px-4 text-center">
                            <div className="flex items-center justify-center gap-1">
                              <Button
                                onClick={() => setDetailModal({ 
                                  isOpen: true, 
                                  title: `Messaggio da ${contact.name}`, 
                                  data: contact 
                                })}
                                size="sm"
                                variant="ghost"
                                className="text-blue-500 hover:text-blue-700"
                                title="Visualizza dettagli"
                              >
                                <Maximize2 className="w-4 h-4" />
                              </Button>
                              <Button
                                onClick={() => deleteRecord('contact', contact.id)}
                                size="sm"
                                variant="ghost"
                                className="text-red-500 hover:text-red-700"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {filterData(contacts, ['name', 'email', 'message']).length === 0 && (
                    <div className="text-center py-8" style={{ color: 'var(--text-muted)' }}>
                      Nessun messaggio di contatto trovato
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="newsletter">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle style={{ color: 'var(--text-primary)' }}>Iscritti Newsletter</CardTitle>
                <Button 
                  onClick={() => exportToCSV(newsletter, 'newsletter_subscribers')} 
                  className="btn-secondary"
                  size="sm"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Esporta CSV
                </Button>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b" style={{ borderColor: 'var(--border-light)' }}>
                        <th className="text-left py-3 px-4 text-sm font-semibold" style={{ color: 'var(--text-secondary)' }}>Email</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold" style={{ color: 'var(--text-secondary)' }}>Status</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold" style={{ color: 'var(--text-secondary)' }}>Data Iscrizione</th>
                        <th className="text-center py-3 px-4 text-sm font-semibold" style={{ color: 'var(--text-secondary)' }}>Azioni</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filterData(newsletter, ['email']).map((subscriber) => (
                        <tr key={subscriber.id} className="border-b hover:bg-opacity-50" style={{ borderColor: 'var(--border-light)' }}>
                          <td className="py-3 px-4" style={{ color: 'var(--text-body)' }}>{subscriber.email}</td>
                          <td className="py-3 px-4">
                            <span className="text-xs px-2 py-1 rounded-full" style={{ 
                              background: subscriber.status === 'active' ? 'var(--accent-wash)' : 'rgba(239, 68, 68, 0.1)', 
                              color: subscriber.status === 'active' ? 'var(--accent-text)' : '#EF4444' 
                            }}>
                              {subscriber.status}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-sm" style={{ color: 'var(--text-muted)' }}>
                            {new Date(subscriber.subscribed_at).toLocaleDateString('it-IT')}
                          </td>
                          <td className="py-3 px-4 text-center">
                            <Button
                              onClick={() => deleteRecord('newsletter', subscriber.id)}
                              size="sm"
                              variant="ghost"
                              className="text-red-500 hover:text-red-700"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {filterData(newsletter, ['email']).length === 0 && (
                    <div className="text-center py-8" style={{ color: 'var(--text-muted)' }}>
                      Nessun iscritto alla newsletter trovato
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* APIs Tab */}
          <TabsContent value="apis">
            {apiBuilderOpen ? (
              <APIBuilder
                api={editingApi}
                onSave={handleSaveApi}
                onCancel={() => {
                  setApiBuilderOpen(false);
                  setEditingApi(null);
                }}
                isLoading={loading}
              />
            ) : (
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle style={{ color: 'var(--text-primary)' }}>Gestione API</CardTitle>
                  <Button 
                    onClick={() => {
                      setEditingApi(null);
                      setApiBuilderOpen(true);
                    }} 
                    className="btn-primary"
                  >
                    <Wrench className="w-4 h-4 mr-2" />
                    Console API
                  </Button>
                </CardHeader>
                <CardContent>
                  {/* APIs Table */}
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-4 font-semibold text-sm" style={{ color: 'var(--text-secondary)' }}>Nome</th>
                        <th className="text-left py-3 px-4 font-semibold text-sm" style={{ color: 'var(--text-secondary)' }}>Categoria</th>
                        <th className="text-left py-3 px-4 font-semibold text-sm" style={{ color: 'var(--text-secondary)' }}>Versione</th>
                        <th className="text-left py-3 px-4 font-semibold text-sm" style={{ color: 'var(--text-secondary)' }}>Status</th>
                        <th className="text-center py-3 px-4 font-semibold text-sm" style={{ color: 'var(--text-secondary)' }}>Azioni</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filterData(apis, ['name', 'category', 'description']).map((api) => (
                        <tr key={api.id} className="border-b hover:bg-gray-50">
                          <td className="py-3 px-4">
                            <div>
                              <div className="font-medium" style={{ color: 'var(--text-primary)' }}>
                                {api.name}
                              </div>
                              <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                                {api.description?.substring(0, 60)}...
                              </div>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-sm" style={{ color: 'var(--text-primary)' }}>
                            {api.category}
                          </td>
                          <td className="py-3 px-4 text-sm font-mono" style={{ color: 'var(--text-primary)' }}>
                            {api.version}
                          </td>
                          <td className="py-3 px-4">
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium" style={{
                              background: api.is_active ? 'var(--accent-wash)' : 'rgba(239, 68, 68, 0.1)', 
                              color: api.is_active ? 'var(--accent-text)' : '#EF4444' 
                            }}>
                              {api.is_active ? '✓ Attiva' : '✗ Inattiva'}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center justify-center gap-2">
                              <Button
                                onClick={() => handleEditApi(api)}
                                size="sm"
                                variant="ghost"
                                title="Modifica"
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                onClick={() => toggleApiStatus(api.id)}
                                size="sm"
                                variant="ghost"
                                title={api.is_active ? 'Disattiva' : 'Attiva'}
                              >
                                <Power className="w-4 h-4" />
                              </Button>
                              <Button
                                onClick={() => deleteApi(api.id)}
                                size="sm"
                                variant="ghost"
                                className="text-red-500 hover:text-red-700"
                                title="Elimina"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {filterData(apis, ['name', 'category', 'description']).length === 0 && (
                    <div className="text-center py-8" style={{ color: 'var(--text-muted)' }}>
                      Nessuna API trovata
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
            )}
          </TabsContent>

          {/* Developers Tab */}
          <TabsContent value="developers">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle style={{ color: 'var(--text-primary)' }}>Sviluppatori Registrati</CardTitle>
                <Button 
                  onClick={() => exportToCSV(developers, 'developers')} 
                  className="btn-secondary"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Esporta CSV
                </Button>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-4 font-semibold text-sm" style={{ color: 'var(--text-secondary)' }}>Nome</th>
                        <th className="text-left py-3 px-4 font-semibold text-sm" style={{ color: 'var(--text-secondary)' }}>Email</th>
                        <th className="text-left py-3 px-4 font-semibold text-sm" style={{ color: 'var(--text-secondary)' }}>Azienda</th>
                        <th className="text-left py-3 px-4 font-semibold text-sm" style={{ color: 'var(--text-secondary)' }}>Ruolo</th>
                        <th className="text-left py-3 px-4 font-semibold text-sm" style={{ color: 'var(--text-secondary)' }}>Approvato</th>
                        <th className="text-left py-3 px-4 font-semibold text-sm" style={{ color: 'var(--text-secondary)' }}>Status</th>
                        <th className="text-left py-3 px-4 font-semibold text-sm" style={{ color: 'var(--text-secondary)' }}>Registrato</th>
                        <th className="text-center py-3 px-4 font-semibold text-sm" style={{ color: 'var(--text-secondary)' }}>Azioni</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filterData(developers, ['first_name', 'last_name', 'email', 'company']).map((dev) => (
                        <tr key={dev.id} className="border-b hover:bg-gray-50">
                          <td className="py-3 px-4 font-medium" style={{ color: 'var(--text-primary)' }}>
                            {dev.first_name} {dev.last_name}
                          </td>
                          <td className="py-3 px-4 text-sm" style={{ color: 'var(--text-secondary)' }}>
                            {dev.email}
                          </td>
                          <td className="py-3 px-4 text-sm" style={{ color: 'var(--text-primary)' }}>
                            {dev.company}
                          </td>
                          <td className="py-3 px-4 text-sm" style={{ color: 'var(--text-secondary)' }}>
                            {dev.role}
                          </td>
                          <td className="py-3 px-4">
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium" style={{
                              background: dev.is_approved ? 'rgba(34, 197, 94, 0.1)' : 'rgba(251, 191, 36, 0.1)', 
                              color: dev.is_approved ? '#16A34A' : '#D97706' 
                            }}>
                              {dev.is_approved ? '✓ Approvato' : '⏳ In attesa'}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium" style={{
                              background: dev.is_active ? 'var(--accent-wash)' : 'rgba(239, 68, 68, 0.1)', 
                              color: dev.is_active ? 'var(--accent-text)' : '#EF4444' 
                            }}>
                              {dev.is_active ? '✓ Attivo' : '✗ Disattivo'}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-sm" style={{ color: 'var(--text-muted)' }}>
                            {new Date(dev.created_at).toLocaleDateString('it-IT')}
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center justify-center gap-2">
                              {!dev.is_approved && (
                                <Button
                                  onClick={() => approveDeveloper(dev.id)}
                                  size="sm"
                                  variant="ghost"
                                  className="text-green-600 hover:text-green-700"
                                  title="Approva"
                                >
                                  <CheckCircle className="w-4 h-4" />
                                </Button>
                              )}
                              <Button
                                onClick={() => toggleDeveloperStatus(dev.id)}
                                size="sm"
                                variant="ghost"
                                title={dev.is_active ? 'Disattiva' : 'Attiva'}
                              >
                                <Power className="w-4 h-4" />
                              </Button>
                              <Button
                                onClick={() => deleteDeveloper(dev.id)}
                                size="sm"
                                variant="ghost"
                                className="text-red-500 hover:text-red-700"
                                title="Elimina"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {filterData(developers, ['first_name', 'last_name', 'email', 'company']).length === 0 && (
                    <div className="text-center py-8" style={{ color: 'var(--text-muted)' }}>
                      Nessuno sviluppatore trovato
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* SMTP Configuration Tab */}
          <TabsContent value="smtp">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle style={{ color: 'var(--text-primary)' }}>Configurazione SMTP</CardTitle>
                    <p className="text-sm mt-2" style={{ color: 'var(--text-secondary)' }}>
                      Configura il server SMTP (Brevo) per l'invio automatico delle email di notifica ed etichette
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>Status:</span>
                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium" style={{
                      background: smtpConfig.is_active ? 'var(--accent-wash)' : 'rgba(239, 68, 68, 0.1)', 
                      color: smtpConfig.is_active ? 'var(--accent-text)' : '#EF4444' 
                    }}>
                      {smtpConfig.is_active ? '✓ Attivo' : '✗ Inattivo'}
                    </span>
                    <Button
                      onClick={toggleSMTPStatus}
                      variant="outline"
                      size="sm"
                    >
                      {smtpConfig.is_active ? 'Disattiva' : 'Attiva'}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div
                  data-testid="smtp-brevo-hint"
                  className="mb-6 rounded-lg p-4 text-sm"
                  style={{ background: 'var(--accent-wash)', color: 'var(--text-secondary)' }}
                >
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div>
                      <strong style={{ color: 'var(--text-primary)' }}>Preset Brevo consigliato</strong>
                      <p className="mt-1">
                        Host <code>smtp-relay.brevo.com</code> · Porta <code>587</code> · TLS attivo · Username = login Brevo (email) · Password = <em>SMTP key</em> Brevo. L'email mittente deve appartenere a un dominio/sender verificato su Brevo.
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      data-testid="smtp-brevo-preset-button"
                      onClick={() => setSmtpConfig({ ...smtpConfig, smtp_host: 'smtp-relay.brevo.com', smtp_port: 587, use_tls: true })}
                    >
                      Usa preset Brevo
                    </Button>
                  </div>
                </div>
                <form onSubmit={handleSaveSMTPConfig} className="space-y-6">
                  {/* SMTP Server Settings */}
                  <div>
                    <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                      Impostazioni Server SMTP
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">Host SMTP *</label>
                        <Input
                          type="text"
                          value={smtpConfig.smtp_host}
                          onChange={(e) => setSmtpConfig({ ...smtpConfig, smtp_host: e.target.value })}
                          required
                          placeholder="smtp-relay.brevo.com"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Porta *</label>
                        <Input
                          type="number"
                          value={smtpConfig.smtp_port}
                          onChange={(e) => setSmtpConfig({ ...smtpConfig, smtp_port: parseInt(e.target.value) })}
                          required
                          placeholder="587"
                        />
                        <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                          Porta 587 (TLS) o 465 (SSL)
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Authentication */}
                  <div>
                    <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                      Autenticazione
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">Username / Email *</label>
                        <Input
                          type="text"
                          value={smtpConfig.smtp_username}
                          onChange={(e) => setSmtpConfig({ ...smtpConfig, smtp_username: e.target.value })}
                          required
                          placeholder="la tua login Brevo (email)"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Password *</label>
                        <Input
                          type="password"
                          value={smtpConfig.smtp_password}
                          onChange={(e) => setSmtpConfig({ ...smtpConfig, smtp_password: e.target.value })}
                          required={!smtpConfig.smtp_host}
                          placeholder="••••••••"
                        />
                        <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                          Lascia vuoto per mantenere la password attuale
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Sender Settings */}
                  <div>
                    <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                      Mittente Email
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">Email Mittente *</label>
                        <Input
                          type="email"
                          value={smtpConfig.smtp_from_email}
                          onChange={(e) => setSmtpConfig({ ...smtpConfig, smtp_from_email: e.target.value })}
                          required
                          placeholder="noreply@spedireonline.net"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Nome Mittente *</label>
                        <Input
                          type="text"
                          value={smtpConfig.smtp_from_name}
                          onChange={(e) => setSmtpConfig({ ...smtpConfig, smtp_from_name: e.target.value })}
                          required
                          placeholder="SpedireOnline.net"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Security Settings */}
                  <div>
                    <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                      Sicurezza
                    </h3>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="use_tls"
                        checked={smtpConfig.use_tls}
                        onChange={(e) => setSmtpConfig({ ...smtpConfig, use_tls: e.target.checked })}
                      />
                      <label htmlFor="use_tls" className="text-sm font-medium">
                        Usa TLS/STARTTLS (raccomandato)
                      </label>
                    </div>
                    <p className="text-xs mt-2" style={{ color: 'var(--text-muted)' }}>
                      La maggior parte dei provider SMTP richiede TLS sulla porta 587
                    </p>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-4 pt-4 border-t">
                    <Button type="submit" className="btn-primary" disabled={smtpLoading}>
                      {smtpLoading ? 'Salvataggio...' : 'Salva Configurazione'}
                    </Button>
                  </div>
                </form>

                {/* Notification Recipients Section */}
                <div className="mt-8 pt-8 border-t" data-testid="notification-emails-section">
                  <h3 className="text-lg font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
                    <Mail className="w-5 h-5 inline mr-2" />
                    Email Aziendali per Notifiche
                  </h3>
                  <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>
                    Indirizzi aziendali che ricevono le notifiche automatiche: nuove registrazioni sviluppatori,
                    nuove richieste demo e nuovi messaggi di contatto.
                  </p>

                  <div className="flex gap-3 mb-4">
                    <div className="flex-1">
                      <Input
                        type="email"
                        value={newNotifEmail}
                        onChange={(e) => setNewNotifEmail(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addNotificationEmail(); } }}
                        placeholder="es. notifiche@spedireonline.net"
                        data-testid="notification-email-input"
                      />
                    </div>
                    <Button type="button" variant="outline" onClick={addNotificationEmail} data-testid="add-notification-email-btn">
                      Aggiungi
                    </Button>
                  </div>

                  {smtpConfig.notification_emails.length === 0 ? (
                    <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
                      Nessuna email configurata. Le notifiche admin non verranno inviate finché non aggiungi almeno un destinatario.
                    </p>
                  ) : (
                    <div className="flex flex-wrap gap-2 mb-4">
                      {smtpConfig.notification_emails.map((email) => (
                        <span
                          key={email}
                          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm"
                          style={{ background: 'var(--accent-wash)', color: 'var(--accent-text)' }}
                          data-testid={`notification-email-chip-${email}`}
                        >
                          {email}
                          <button
                            type="button"
                            onClick={() => removeNotificationEmail(email)}
                            className="hover:opacity-70"
                            aria-label={`Rimuovi ${email}`}
                            data-testid={`remove-notification-email-${email}`}
                          >
                            ✕
                          </button>
                        </span>
                      ))}
                    </div>
                  )}

                  <Button
                    type="button"
                    className="btn-primary"
                    onClick={handleSaveNotificationEmails}
                    disabled={notifLoading}
                    data-testid="save-notification-emails-btn"
                  >
                    {notifLoading ? 'Salvataggio...' : 'Salva Email Notifiche'}
                  </Button>
                  {!smtpConfig.is_active && (
                    <p className="text-xs mt-2 text-amber-600">
                      ⚠️ Le notifiche verranno inviate solo quando la configurazione SMTP è "Attiva".
                    </p>
                  )}
                </div>

                {/* Test Email Section */}
                <div className="mt-8 pt-8 border-t">
                  <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                    <Send className="w-5 h-5 inline mr-2" />
                    Test Configurazione
                  </h3>
                  <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>
                    Invia un'email di test per verificare che la configurazione SMTP funzioni correttamente
                  </p>
                  <div className="flex gap-4">
                    <div className="flex-1">
                      <Input
                        type="email"
                        value={testEmail}
                        onChange={(e) => setTestEmail(e.target.value)}
                        placeholder="Inserisci email per test"
                        disabled={!smtpConfig.is_active}
                      />
                    </div>
                    <Button
                      onClick={handleTestSMTP}
                      variant="outline"
                      disabled={!testEmail || !smtpConfig.is_active || smtpLoading}
                    >
                      <Send className="w-4 h-4 mr-2" />
                      Invia Test
                    </Button>
                  </div>
                  {!smtpConfig.is_active && (
                    <p className="text-xs mt-2 text-amber-600">
                      ⚠️ Attiva la configurazione SMTP per poter inviare email di test
                    </p>
                  )}
                </div>

                {/* Info Box */}
                <div className="mt-6 p-4 rounded-lg" style={{ background: 'var(--accent-wash)' }}>
                  <div className="flex items-start gap-3">
                    <Mail className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: 'var(--accent-primary)' }} />
                    <div>
                      <p className="font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                        Notifiche Email Automatiche
                      </p>
                      <ul className="text-sm space-y-1" style={{ color: 'var(--text-secondary)' }}>
                        <li>• Email di benvenuto agli sviluppatori registrati</li>
                        <li>• Notifica di approvazione account allo sviluppatore</li>
                        <li>• Notifica alle email aziendali: nuova registrazione sviluppatore</li>
                        <li>• Notifica alle email aziendali: nuova richiesta demo</li>
                        <li>• Notifica alle email aziendali: nuovo messaggio di contatto</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="integrations">
            <IntegrationsConfig token={token} API={API} />
          </TabsContent>

          <TabsContent value="shipments">
            <AdminShipments token={token} API={API} />
          </TabsContent>
        </Tabs>
      </div>
      
      {/* Detail Modal */}
      <DetailModal 
        isOpen={detailModal.isOpen}
        onClose={() => setDetailModal({ isOpen: false, title: '', data: null })}
        title={detailModal.title}
        data={detailModal.data}
      />
    </div>
  );
};

export default AdminPanel;
